import { Badge, CircularProgress, Tab, Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useGetDeliverablesQuery } from '../../app/athenaApi';
import {
  setAllowedActions,
  setPage,
  setPageSize,
  setSelectedRowId,
  setViewPanel,
} from '../../app/deliverables/deliverablesSlice';
import { RootState } from '../../app/store';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../utils/tableVariables';
import DeliverablesTable from './components/DeliverablesTable/DeliverablesTable';
import { Header } from './components/Header';
import AddDeliverableModal from './components/Modal/AddDeliverable/AddDeliverableModal';
import { DeliverableModalProvider } from './components/Modal/DeliverableModalContext';
import EditDeliverableModal from './components/Modal/EditDeliverable/EditDeliverableModal';
import ViewPanel from './components/ViewPanel/ViewPanel';
import {
  DeliverablesContainer,
  DeliverablesTableContainer,
} from './StyledComponents';

type TDeliverablesProps = {
  readOnly: boolean;
};

const Deliverables = (props: TDeliverablesProps) => {
  const tabs = ['View All', 'In Draft', 'Submitted', 'Completed'];
  const { actions } = useSelector((state: RootState) => state.user);
  const { expandedPanel, projectIdFilter, typeIdFilter, authorIdFilter } =
    useSelector((state: RootState) => state.deliverables);
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useDispatch();
  const [isBadgeLoading, setIsBadgeLoading] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<number[]>([]);

  // Deep-link support: `?deliverableId=X` selects that deliverable and opens
  // the View Panel. The Project page's ViewPanel sends users here for non-
  // form deliverables (PMP, BOM, etc.) that don't have a dedicated detail
  // route. The ViewPanel re-fetches by ID, so the deliverable doesn't have
  // to be on the current table page for selection to work. URL param is
  // cleared after consumption so refresh/back doesn't re-trigger.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkDeliverableId = searchParams.get('deliverableId');
  useEffect(() => {
    if (!deepLinkDeliverableId) return;
    dispatch(setSelectedRowId(deepLinkDeliverableId));
    dispatch(setViewPanel(true));
    searchParams.delete('deliverableId');
    setSearchParams(searchParams, { replace: true });
  }, [deepLinkDeliverableId, dispatch, searchParams, setSearchParams]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    dispatch(setPage(0));
    dispatch(setPageSize(DEFAULT_TABLE_PAGE_SIZE));
    setTabValue(newValue);
  };

  const tabArgs = useMemo(() => {
    const common = {
      page: 1,
      limit: 1,
      projectId: projectIdFilter || undefined,
      deliverableTypeId: typeIdFilter || undefined,
      authorId: authorIdFilter || undefined,
    };
    return [
      { ...common, workflowState: [] }, // View All
      { ...common, workflowState: ['Pending'] }, // In Draft
      {
        ...common,
        workflowState: [
          'In Internal Review',
          'In APM Review',
          'In COR Review',
          'In Theater Director Review',
          // Legacy state kept for environments not yet cut over to 0.29.0.
          'In Federal Review',
        ],
      }, // Submitted
      {
        ...common,
        workflowState: [
          'Federally Approved',
          'Internally Approved',
          'No Workflow, Approved',
          'Internally Rejected',
          'Federally Rejected',
        ],
      }, // Completed
    ];
  }, [projectIdFilter, typeIdFilter, authorIdFilter]);

  const qAll = useGetDeliverablesQuery(tabArgs[0]);
  const qPending = useGetDeliverablesQuery(tabArgs[1]);
  const qSubmitted = useGetDeliverablesQuery(tabArgs[2]);
  const qCompleted = useGetDeliverablesQuery(tabArgs[3]);

  const counts = useMemo(
    () => [
      qAll?.data?.total ?? 0,
      qPending?.data?.total ?? 0,
      qSubmitted?.data?.total ?? 0,
      qCompleted?.data?.total ?? 0,
    ],
    [
      qAll?.data?.total,
      qPending?.data?.total,
      qSubmitted?.data?.total,
      qCompleted?.data?.total,
    ],
  );

  const anyLoading = Boolean(
    qAll?.isLoading ||
      qAll?.isFetching ||
      qPending?.isLoading ||
      qPending?.isFetching ||
      qSubmitted?.isLoading ||
      qSubmitted?.isFetching ||
      qCompleted?.isLoading ||
      qCompleted?.isFetching,
  );

  useEffect(() => {
    setIsBadgeLoading(anyLoading);
    setBadgeCounts(counts);
  }, [anyLoading, counts]);

  useEffect(() => {
    const userAllowedActions = actions?.filter((action) =>
      action?.scope?.includes('Deliverable'),
    );
    dispatch(setAllowedActions(userAllowedActions));
  }, []);

  return (
    <DeliverablesContainer>
      {!props.readOnly && <Header />}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          borderColor: 'divider',
          paddingTop: 1,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="deliverables tabs"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 'auto',
            '& .MuiTabs-indicator': {
              backgroundColor: '#1f4D83',
            },
            '& .MuiTab-root': {
              color: 'text.primary',
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-visible': {
                outline: 'none',
              },
              '& .Mui-selected': {
                outline: 'none',
                color: '#1f4D83',
                '& span': {
                  color: '#1f4D83',
                },
              },
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              sx={{
                textTransform: 'none',
                minHeight: 'auto',
                py: 1.5,
              }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    justifyContent: 'center',
                  }}
                >
                  <span>{tab}</span>
                  {isBadgeLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Badge
                      badgeContent={badgeCounts[index] ?? 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#1F4D83',
                          color: '#FFFFFF',
                        },
                      }}
                      showZero
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      <DeliverablesTableContainer>
        {!expandedPanel && (
          <DeliverablesTable
            readOnly={props.readOnly}
            workflowState={tabArgs[tabValue].workflowState}
          />
        )}
        <ViewPanel />
      </DeliverablesTableContainer>
      <DeliverableModalProvider>
        <AddDeliverableModal />
        <EditDeliverableModal />
      </DeliverableModalProvider>
    </DeliverablesContainer>
  );
};

export default Deliverables;
